import { gql, useMutation, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import { FeatureFlagTypes } from '@generated/graphql';
import type { Account } from '@interfaces/account';
import type { WorkflowCompletion } from '@interfaces/workflow';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import type { WorkflowCompletionWithId } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionWithId.types';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { FlagsmithFlags } from '@utils/flagsmith';
import { resolveReviewAccountQuery } from '@utils/resolveReviewAccountQuery';
import { Box, Button, Skeleton, Typography } from 'hephaestus/web';
import { Loop } from 'hephaestus/webIcons';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { useOrganizationFeatureFlag } from '../../../../../hooks/useOrganizationFeatureFlag';
import { GO_TO_STEP } from '../queries';
import {
  type ReviewAccountSubstepOptions,
  ReviewAccountVisual,
} from './reviewAccount.visual';

export const WORKFLOW_COMPLETION = `
  workflowCompletion {
    id
    objectId
    context
    workflow {
      name { ${TRANSLATED_STRING_FIELDS} }
      steps {
        id
        name { ${TRANSLATED_STRING_FIELDS} }
        subSteps {
          id
          type
        }
      }
    }
    currentStep {
      id
      name { ${TRANSLATED_STRING_FIELDS} }
      subSteps {
        id
        type
      }
    }
    currentSubStep {
      id
      options
      type
      canGoBack
      skippable
      rolesCompleteableBy {
        id
      }
    }
  }
`;

const FETCH_ACCOUNTS = gql`
  query fetchAccounts($input: FetchAccountsInput!) {
    fetchAccounts(input: $input) {
      accounts {
        id: stringId
        type
        applyForGovFunds
        affiliations {
          id
          allocation
          relation
          type
          user {
            id: stringId
            firstName
            lastName
          }
        }
      }
    }
  }
`;

export const ReviewAccount = ({
  options,
  onNext,
  setActiveWorkflowCompletion,
  workflowCompletion,
  userId,
}: {
  options: ReviewAccountSubstepOptions;
  onNext: any;
  setActiveWorkflowCompletion: any;
  workflowCompletion: WorkflowCompletion;
  userId: string;
}) => {
  const { translateBackendString } = useLocalization();
  const { sendAnalytic } = useContext(AnalyticsContext);
  const allAccountEditInReviewEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.AllAccountEditInReview,
  );
  const [accounts, setAccounts] = useState<Account[]>();
  const { t } = useTranslation([
    'workflowCompletions',
    'accountsDetail',
    'affiliationTypes',
    'accountTypes',
  ]);
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const contextAware = useFeatureFlag(FlagsmithFlags.context_aware_workflows);
  const currentUserId = workflowData.currentUserId || userId;
  const reviewQuery = resolveReviewAccountQuery({
    workflowData,
    objectType: workflowCompletion.objectType,
    contextAware,
    userId: currentUserId,
  });
  const { data, loading: loadingAccounts } = useQuery(FETCH_ACCOUNTS, {
    variables: {
      input: {
        filter: {
          userId: currentUserId,
          states: reviewQuery.states,
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    skip: !reviewQuery.shouldFetch,
    fetchPolicy: 'no-cache',
  });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(loadingAccounts);
  }, [loadingAccounts, setLoading]);

  const [goToStep] = useMutation(GO_TO_STEP());

  useEffect(() => {
    if (data) {
      setAccounts(data.fetchAccounts.accounts);
    }
  }, [data]);
  const goToAccountCreate = async () => {
    setLoading(true);
    const subSteps = (workflowCompletion?.workflow?.steps || []).flatMap(
      (x: any) => x.subSteps,
    );
    const accountForGoalCreateSubStep = subSteps.find(
      (x: any) => x.type === 'CREATE_ACCOUNT_FOR_GOAL',
    );
    const accountCreateSubStep = subSteps.find(
      (x: any) => x.type === 'CREATE_ACCOUNT',
    );
    const selectedSubStep = accountForGoalCreateSubStep ?? accountCreateSubStep;
    await setWorkflowData({
      ...workflowData,
      currentGoalId: accountCreateSubStep
        ? undefined
        : workflowData.currentGoalId,
      currentAccountId: undefined,
      currentAccountType: undefined,
      currentAccountState: undefined,
      currentSubAccountId: undefined,
    });
    const { data: goToStepData } = await goToStep({
      variables: {
        input: {
          workflowCompletionId: workflowCompletion.id,
          subStepId: selectedSubStep.id,
        },
      },
    });
    setActiveWorkflowCompletion(goToStepData.goToSubStep.workflowCompletion);
    setLoading(false);
  };
  const onSubmit = async () => {
    sendAnalytic(ovAnalyticsEvents.workflowsReviewAccountContinueButtonSelect, {
      workflowStepTitle: options?.title,
      workflowId: workflowCompletion?.workflow?.id,
      workflowName: workflowCompletion?.workflow?.name,
      activeWorkflowCompletionId: workflowCompletion?.id,
      objectId: workflowCompletion?.objectId,
      objectType: workflowCompletion?.objectType,
    });
    await onNext();
  };

  return (
    <WorkflowCompletionForm
      onSubmit={onSubmit}
      buttonFooter={
        options.createAnotherAccount && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              flex: 1,
            }}
          >
            <Button
              data-testid='review-account-create-another-button'
              variant='contained'
              sx={{ mt: 3, mr: 1 }}
              disabled={loading}
              onClick={goToAccountCreate}
              startIcon={<Loop />}
            >
              {t('createAnotherAccount')}
            </Button>
          </Box>
        )
      }
      submitButtonProps={{
        label: t('continue'),
        disabled: loading,
        dataTestId: 'review-account-continue-button',
      }}
    >
      {allAccountEditInReviewEnabled ? (
        <ReviewAccountVisual
          options={options}
          userId={userId}
          workflowCompletion={workflowCompletion as WorkflowCompletionWithId}
        />
      ) : (
        <>
          <Typography variant='displayLarge' sx={{ mt: 1 }}>
            {translateBackendString(options?.title)}
          </Typography>
          <Typography variant='bodyLarge' sx={{ mb: 3 }}>
            {translateBackendString(options?.subtitle)}
          </Typography>
          {loading || reviewQuery.isWaitingForAccount ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Skeleton width={250} />
                <Skeleton width={150} />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Skeleton width={250} />
                <Skeleton width={150} />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Skeleton width={250} />
                <Skeleton width={150} />
              </Box>
            </>
          ) : (
            <>
              {accounts &&
                accounts.length > 0 &&
                accounts
                  .filter((account: Account) =>
                    reviewQuery.accountIds.includes(account.id),
                  )
                  .map((account: Account) => (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 2,
                        }}
                      >
                        <Typography variant='bodyLarge'>
                          {t('reviewAccount.type')}
                        </Typography>
                        <Typography
                          variant='bodyLarge'
                          sx={{ fontWeight: 'bold', textAlign: 'right' }}
                        >
                          {t(`accountTypes:${account?.type}`)}
                        </Typography>
                      </Box>

                      {account?.applyForGovFunds?.length > 0 &&
                        account.applyForGovFunds.map(
                          (govFund: any, index: number) => (
                            <Box
                              key={index}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mb: 2,
                              }}
                            >
                              <Typography variant='bodyLarge'>
                                {t(`accountsDetail:govFundOptions.${govFund}`)}
                              </Typography>
                              <Typography
                                variant='bodyLarge'
                                sx={{ fontWeight: 'bold', textAlign: 'right' }}
                              >
                                {t('applied')}
                              </Typography>
                            </Box>
                          ),
                        )}

                      {account &&
                        account?.affiliations?.map((affiliation: any) => (
                          <Box
                            key={affiliation.id}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mb: 2,
                            }}
                          >
                            <Typography variant='bodyLarge'>
                              {t(
                                `affiliationTypes:${
                                  affiliation.type === 'OTHER'
                                    ? affiliation.relation
                                    : affiliation.type
                                }`,
                              )}
                            </Typography>
                            <Typography
                              variant='bodyLarge'
                              sx={{ fontWeight: 'bold', textAlign: 'right' }}
                            >
                              {affiliation.user.firstName}{' '}
                              {affiliation.user.lastName}
                            </Typography>
                          </Box>
                        ))}
                    </>
                  ))}
            </>
          )}
        </>
      )}
    </WorkflowCompletionForm>
  );
};

export default ReviewAccount;
